import { isBuoyDevice } from 'features/Integrations/features/ConnectedDevices/features/Buoy/utils/buoyDevice.utils';
import { USGSMeasureNormalizer } from 'features/Integrations/features/ConnectedDevices/features/DeviceGraph/utils/measurement.utils';
import { usgsGetMostRecentValues } from 'features/Integrations/features/ConnectedDevices/utils/usgsData.utils';
import { ResolveDeviceType } from 'features/Integrations/utils/deviceComparison.utils';
import { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { fetchStageImageObjectUrl } from 'features/Integrations/utils/stageImage.utils';
import { extractDateTimeFromPict } from 'utils/dates/date.utils';

function hasValue(value) {
  return value !== null && value !== undefined;
}

function formatNumber(value, digits = 3) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : 'N/A';
}

function MetricGrid({ rows }) {
  const visibleRows = rows.filter(Boolean);
  if (!visibleRows.length) return <div className="text-muted small">No datapoints available.</div>;

  return (
    <div className="d-grid">
      {visibleRows.map((row) => (
        <div
          key={row.label}
          className="d-flex align-items-start justify-content-between gap-2 py-1 border-bottom"
        >
          <span className="d-inline-flex align-items-start gap-2 text-secondary fw-semibold small lh-sm">
            {row.color && (
              <span
                className="connected-device-map-popup__metric-accent"
                style={{ backgroundColor: row.color }}
                aria-hidden="true"
              />
            )}
            <span>{row.label}</span>
          </span>
          <span className="small fw-semibold text-end text-break lh-sm">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function MetricDetailsBox({ rows, previewCount = 2, expanded = false }) {
  const visibleRows = rows.filter(Boolean);
  const previewRows = visibleRows.slice(0, previewCount);
  const detailRows = visibleRows.slice(previewCount);

  if (visibleRows.length <= previewCount) {
    return <MetricGrid rows={visibleRows} />;
  }

  return (
    <div className="d-grid gap-1">
      <MetricGrid rows={previewRows} />
      <Collapse in={expanded}>
        <div>
          <MetricGrid rows={detailRows} />
        </div>
      </Collapse>
    </div>
  );
}

export default function DeviceMapPopupContent({ device, detailsExpanded = false }) {
  if (isBuoyDevice(device)) {
    return <RenderBuoyMapDeviceData device={device} detailsExpanded={detailsExpanded} />;
  }

  switch (ResolveDeviceType(device)) {
    case 'Stage':
      return <RenderStageMapDeviceData device={device} detailsExpanded={detailsExpanded} />;
    case 'Monitoring Station':
      return <RenderUSGSMapDeviceData device={device} detailsExpanded={detailsExpanded} />;
    default:
      return <RenderRescAlertMapDeviceData device={device} />;
  }
}

function RenderRescAlertMapDeviceData({ device }) {
  return (
    <div className="p-2 text-secondary bg-light border rounded-2">
      <div className="fw-semibold mb-1">Comments</div>
      <div>
        {device?.data?.[0]?.comments || <span className="text-muted">No comments available.</span>}
      </div>
    </div>
  );
}

function RenderStageMapDeviceData({ device, detailsExpanded }) {
  const deviceData = device?.data?.[0];
  const [imageUrl, setImageUrl] = useState(null);
  const hasDepth1 = hasValue(deviceData?.depth1);
  const hasWaterLevelAdjustment = hasValue(device?.water_level_adj);
  const hasWaterTemperature = hasValue(deviceData?.h2oTemp);
  const hasBarometricPressure = hasValue(deviceData?.baro);
  const hasBattery = hasValue(deviceData?.battery);
  const hasAlertIndicator =
    (hasValue(deviceData?.ffi1) && Boolean(deviceData?.ffi1)) ||
    (hasValue(deviceData?.drInd) && Boolean(deviceData?.drInd)) ||
    (hasValue(deviceData?.depthInd) && Boolean(deviceData?.depthInd));

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      if (!deviceData?.img_link_url) {
        setImageUrl(null);
        return;
      }

      objectUrl = await fetchStageImageObjectUrl(deviceData.img_link_url);

      if (!isMounted) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return;
      }

      setImageUrl(objectUrl);
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deviceData?.img_link_url]);

  const actualDepth =
    hasDepth1 && hasWaterLevelAdjustment
      ? `${deviceData.depth1 + device.water_level_adj} in / ${formatNumber(
          (deviceData.depth1 + device.water_level_adj) / 12,
        )} ft`
      : 'N/A';

  return (
    <div className="d-flex flex-column gap-3">
      <MetricDetailsBox
        expanded={detailsExpanded}
        rows={[
          { label: 'Actual Depth', value: actualDepth, color: '#ffff00' },
          {
            label: 'Depth',
            value: hasDepth1
              ? `${deviceData.depth1} in / ${formatNumber(deviceData.depth1 / 12)} ft`
              : 'N/A',
            color: '#0075ff',
          },
          {
            label: 'Temperature',
            value: hasWaterTemperature
              ? `${deviceData.h2oTemp} C / ${formatNumber((deviceData.h2oTemp * 9) / 5 + 32, 2)} F`
              : 'N/A',
            color: '#62f671',
          },
          {
            label: 'Pressure',
            value: hasBarometricPressure
              ? `${deviceData.baro} mBar / ${formatNumber(deviceData.baro * 0.02952998)} inHg`
              : 'N/A',
            color: '#7f00ff',
          },
          {
            label: 'Battery',
            value: hasBattery ? `${deviceData.battery}V` : 'N/A',
            color: '#afb943',
          },
          { label: 'Alert Indicated', value: hasAlertIndicator ? 'True' : 'False', color: 'red' },
        ]}
      />

      {imageUrl && deviceData?.img_link_url && (
        <figure className="m-0 overflow-hidden border rounded-2 bg-light">
          <img
            src={imageUrl}
            alt="Device capture"
            className="d-block w-100 object-fit-cover"
            style={{ maxHeight: 140 }}
          />
          <figcaption className="px-2 py-1 text-muted small">
            Image Timestamp: {extractDateTimeFromPict(deviceData.img_link_url)}
          </figcaption>
        </figure>
      )}
    </div>
  );
}

function RenderUSGSMapDeviceData({ device, detailsExpanded }) {
  const mostRecent = usgsGetMostRecentValues(device) || {};
  const latest = Object.entries(mostRecent).map(([timeSeriesId, data]) => {
    const isUnavailable = data?.value === null;
    const normalized = isUnavailable
      ? null
      : USGSMeasureNormalizer(data?.value, data?.unit_of_measure);
    return {
      timeseries: timeSeriesId,
      label: data?.parameterName ?? 'Unknown',
      value: isUnavailable ? 'UNAVAILABLE' : (data?.value ?? 'N/A'),
      unit: normalized?.unit ?? data?.unit_of_measure ?? '',
      altValue: normalized?.alt_value ?? '',
      altUnit: normalized?.alt_unit ?? '',
      validValue: normalized != null && normalized?.value !== null,
      showAlt: normalized?.alt_value !== null,
      ts: data?.ts ?? null,
    };
  });

  if (!latest.length) return <div className="text-muted small">No USGS datapoints available.</div>;

  return (
    <MetricDetailsBox
      expanded={detailsExpanded}
      rows={latest.map((data) => ({
        label: data.label,
        value: (
          <>
            {String(data.value)}
            {data.validValue && data.unit ? ` ${data.unit}` : null}
            {data.showAlt ? ` / ${data.altValue}${data.altUnit ? ` ${data.altUnit}` : ''}` : null}
            <span className="d-block mt-1 text-muted fw-normal" style={{ fontSize: '0.68rem' }}>
              {formatTimestamp(data.ts)}
            </span>
          </>
        ),
      }))}
      previewCount={1}
    />
  );
}

function RenderBuoyMapDeviceData({ device, detailsExpanded }) {
  const latest = device?.data?.[0] || {};
  const safeValue = (val, unit = '') => {
    if (val === undefined || val === null || val === '' || val === '*' || Number.isNaN(val)) {
      return '*';
    }
    return unit ? `${val} ${unit}` : val;
  };

  return (
    <MetricDetailsBox
      expanded={detailsExpanded}
      rows={[
        { label: 'Wind Speed', value: safeValue(latest?.wind_speed, 'MPH') },
        { label: 'Wave Height', value: safeValue(latest?.wave_height, 'ft') },
        { label: 'Water Temp', value: safeValue(latest?.water_temperature, 'F') },
        { label: 'Air Temp', value: safeValue(latest?.air_temperature, 'F') },
        { label: 'Wind Direction', value: safeValue(latest?.wind_direction) },
        { label: 'Timestamp', value: formatBuoyTimestamp(latest?.ts) },
      ]}
    />
  );
}

function formatTimestamp(raw) {
  if (raw == null) return 'No timestamp';
  const parsed =
    typeof raw === 'number' || /^\d+$/.test(String(raw))
      ? new Date(Number(raw))
      : new Date(String(raw));
  if (Number.isNaN(parsed.getTime())) return 'No timestamp';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(parsed);
}

function formatBuoyTimestamp(raw) {
  if (raw == null) return 'No timestamp';
  const parsed = new Date(Number(raw));
  if (Number.isNaN(parsed.getTime())) return 'No timestamp';
  return parsed.toLocaleString();
}
